import { createContext, useContext, useEffect, useRef } from "react";

import { useStore } from "zustand";

import { useUserSettings } from "components/user/hooks";

import { VisProviderT } from "./types";
import { selectXdaSettings } from "./xdaSettingsSelectors";
import {
  DEFAULT_IPDB_SOURCE,
  DEFAULT_TOPS_MODEL_SOURCE,
  type XdaSettingsState,
  type XdaSettingsStore,
  type XdaSettingsStoreApi,
  createInitialState,
  createXdaSettingsStore
} from "./xdaSettingsStore";

export const XdaSettingsStoreContext = createContext<Maybe<XdaSettingsStoreApi>>(null);

/**
 * Was this provider seeded with a source somebody had actually chosen?
 *
 * Only this provider sees the state the viewer was rebuilt from, and the viewer
 * under it needs the same answer before it records its own sources — a value it
 * was handed is a choice, a value it fell back to is not. Published rather than
 * re-derived, because the store is built once at mount and this has to be the
 * same "once" or the two can disagree.
 *
 * `false` outside a provider, which is the honest answer: nothing was restored.
 */
const XdaRestoredSourcesContext = createContext(false);

function XdaSettingsStoreProvider({ children, initialState }: VisProviderT) {
  const userSettings = useUserSettings();

  const defaultTopsModelSource = userSettings.defaults?.geoSettings?.surfaceSource
    ? [userSettings.defaults?.geoSettings?.surfaceSource]
    : [...DEFAULT_TOPS_MODEL_SOURCE];

  const defaultIpdbSource =
    userSettings.defaults?.geoSettings?.ipdbSource ?? DEFAULT_IPDB_SOURCE;

  // The same test the effect below guards on, captured at mount beside the
  // store it belongs to: a dashboard widget rehydrated from a saved
  // componentState carries these, a brand-new one does not.
  const hasRestoredSourcesRef = useRef(
    initialState?.xda?.settings?.topsModelSource !== undefined
  );

  const storeRef = useRef<Maybe<XdaSettingsStoreApi>>(null);
  if (storeRef.current === null) {
    // No initialState.xda (brand-new widget): apply full factory defaults, patched
    // with the two user-default fields.
    //
    // initialState.xda present: use the caller's settings object AS-IS (matches
    // VisContext's original `{...initialState}` semantics) — topsModelSource/
    // ipdbSource are defined whenever a dashboard has ever been saved (DB-backed
    // widget.componentState always carries them once set). Fill in live user
    // defaults only for the genuinely-unset case (a field truly absent from the
    // saved object) rather than silently overriding an explicit saved value.
    //
    // (Code review — corrected a stale comment here: no "DashboardPane strip"
    // of these fields exists anywhere in this codebase, verified against git
    // history; the 3D-viewer widget's session-preservation test/mechanism the
    // comment likely conflated with is a separate, unrelated code path keyed
    // on `threed::${widgetId}`, not XDA's `xda::${widgetId}`.)
    const initial: XdaSettingsState = !initialState?.xda
      ? createInitialState({
          topsModelSource: defaultTopsModelSource,
          ipdbSource: defaultIpdbSource
        })
      : {
          ...initialState.xda.settings,
          topsModelSource:
            initialState.xda.settings.topsModelSource ?? defaultTopsModelSource,
          ipdbSource: initialState.xda.settings.ipdbSource ?? defaultIpdbSource
        };
    storeRef.current = createXdaSettingsStore(initial);
  }

  // Apply user defaults once they arrive from the API (and whenever they change).
  // The store captures its initial value at mount — before the API fetch resolves.
  // This effect fires after settings load and keeps topsModelSource/ipdbSource in
  // sync with user defaults.
  //
  // Guard: if initialState had an explicit topsModelSource (a DB-saved dashboard),
  // we leave it intact so the saved value takes precedence. User defaults only
  // apply when there is no saved value (new/unsaved dashboard).
  useEffect(() => {
    if (initialState?.xda?.settings?.topsModelSource !== undefined) return;

    const geoSettings = userSettings?.defaults?.geoSettings;
    if (!geoSettings?.id) return;

    const updatedTopsModelSource = geoSettings.surfaceSource
      ? [geoSettings.surfaceSource]
      : [...DEFAULT_TOPS_MODEL_SOURCE];

    const currentSettings = selectXdaSettings(storeRef.current.getState());
    storeRef.current.getState().setXdaSettings({
      ...currentSettings,
      topsModelSource: updatedTopsModelSource,
      ipdbSource: geoSettings.ipdbSource ?? DEFAULT_IPDB_SOURCE
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userSettings?.defaults?.geoSettings?.id,
    userSettings?.defaults?.geoSettings?.surfaceSource,
    userSettings?.defaults?.geoSettings?.ipdbSource
  ]);

  return (
    <XdaSettingsStoreContext.Provider value={storeRef.current}>
      <XdaRestoredSourcesContext.Provider value={hasRestoredSourcesRef.current}>
        {children}
      </XdaRestoredSourcesContext.Provider>
    </XdaSettingsStoreContext.Provider>
  );
}

/**
 * Did the viewer under this provider get its sources from saved state?
 *
 * The cross-section asks before it writes them to the session — see the persist
 * effect in `XdaViewer`.
 */
function useXdaHasRestoredSources(): boolean {
  return useContext(XdaRestoredSourcesContext);
}

/**
 * Hook to select state from the nearest XdaSettingsStore.
 *
 * Usage (Container or Logic Hook — never in a Presenter):
 *   const showData = useXdaSettingsStore(selectShowData);
 *
 * For compound selectors that return new object literals (e.g.
 * selectXdaSettings), wrap the selector with `useShallow` at the call site:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const settings = useXdaSettingsStore(useShallow(selectXdaSettings));
 */
function useXdaSettingsStore<T>(selector: (state: XdaSettingsStore) => T): T {
  const store = useContext(XdaSettingsStoreContext);
  if (!store) {
    throw new Error(
      "useXdaSettingsStore must be used within a <XdaSettingsStoreProvider>"
    );
  }
  return useStore(store, selector);
}

export { XdaSettingsStoreProvider, useXdaSettingsStore, useXdaHasRestoredSources };
